<template>
    <div class="verification-page">
        <div class="text-center">
            <div v-show ="!itsAnIframe">
                <router-link to="/">
                <img
                    src="@/assets/images/logos/accounts-logo.svg"
                    class="verification-page__logo img-fluid"
                    alt="Tendencys"
                />
            </router-link>
            </div>
            <div v-show="!itsAnIframe">
                <h1 v-if="!error" class="verification-page__title">
                    {{ $t('Identity validation') }}
                </h1>
            </div>
        </div>
        <div v-if="loading" class="text-center py-5">
            <b-spinner />
        </div>
        <template v-else>
            <empty-state v-if="error" :class="error.class || ''" :title="error.title" :text="error.text" :img="error.image || 'bad-connection.svg'" img-size="md" />
            <component v-else :is="currentForm" v-bind="currentBindings" @redirecToStatusPage="redirecToStatusPage" />
        </template>
    </div>
</template>

<script>
import { mapState } from 'vuex';

import EmptyState from '@/components/ui/EmptyState.vue';
import SumSub from '@/components/verifications/providers/SumSub.vue'
import VerificationForm from '@/components/forms/Verification.vue';

import iframeMixin from '@/plugins/mixin/iframe.mixin';
import supportChatUtils from '@/utils/support-chat.utils';

import { IN_REVIEW, ACCEPTED, REJECTED } from '@/constants/verification-statuses';

export default {
    name: 'verification-page',
    components: {
        EmptyState,
        SumSub,
        VerificationForm,
    },
    mixins: [iframeMixin],
    data() {
        return {
            currentBindings: {},
            currentForm: null,
            error: null,
            loading: false,
            verificationId: null,
            resizeObserver: null,
        }
    },
    computed: {
        ...mapState(['language']),
    },
    watch: {
        language() {
            this.getVerification();
        },
    },
    mounted() {
        this.verificationId = this.$route.query.id;

        if (!this.verificationId) {
            this.error = {
                title: `¡${this.$t('Something went wrong')}!`,
                text: this.$t('The request is required to have the {parameter} parameter.', { parameter: 'id' }),
            };
        } else {
            this.getVerification();
        }

        if (this.itsAnIframe) {
            supportChatUtils.hideChat();
        }

        this.startSendingHeight();
    },
    beforeDestroy() {
        this.resizeObserver.disconnect();
    },
    methods: {
        startSendingHeight() {
            this.sendHeight()
            this.resizeObserver = new ResizeObserver(this.sendHeight)
            this.resizeObserver.observe(document.body) 
        },
        sendHeight() {
            const height = document.documentElement.scrollHeight;

            if (window.parent) {
                window.parent.postMessage({ type: 'resize', height }, '*');
            }
        },
        showGeolocationError(text) {
            this.error = {
                image: 'location-error.svg',
                title: `¡${this.$t('Something went wrong')}!`,
                class: 'verification-page__empty-state-location',
                text,
            };
        },
        handleError() {
            this.error = {
                title: `¡${this.$t('Something went wrong')}!`,
                text: this.$t('You may also refresh the page or try again later'),
            };
        },
        redirecToStatusPage({ type, title, message }) {
            const formatted_message = JSON.stringify({ title, message });
            const encoded_message = Buffer.from(formatted_message).toString('base64');

            this.$router.push({
                name: 'verificationStatusPage',
                params: { status: type },
                query: { _v: encoded_message },
            });
        },
        getRedirectStatus(status) {
            if (status === IN_REVIEW) {
                return {
                    type: 'info',
                    title: 'Application in review',
                    message: 'Your application is being reviewed by the provider. This can take between 1 to 10 minutes.'
                };
            }

            if (status === ACCEPTED) {
                return {
                    type: 'success',
                    title: '¡Approved application!',
                    message: 'Your application was approved. Now you can use the system.'
                };
            }

            if (status === REJECTED) {
                return {
                    type: 'error',
                    title: '¡Application rejected!',
                    message: 'Your application was rejected by the provider.'
                };
            }

            return null;
        },
        loadFormComponent(verification) {
            const available_services = {
                sumsub: 'SumSub',
                accounts: 'VerificationForm',

            };

            if (verification.provider) {
                const { service } = verification.provider;
                this.currentForm = available_services[service];
            }

            /* if (verification?.forms?.form?.length) {
                this.currentForm = available_services.verification_form;
            } */

            this.currentBindings = { verification };
        },
        async getVerification() {
            try {
                this.loading = true;

                const config = { headers: { 'Accept-Language': this.language } };
                const { data } = await this.axiosAccount.get(`/api/verifications/${this.verificationId}`, config);
                const redirect_status = this.getRedirectStatus(data.status);

                if (redirect_status) {
                    window.parent.postMessage({ type: 'showing-status-page', status: data.status }, '*');
                    this.redirecToStatusPage(redirect_status);
                } else {
                    this.loadFormComponent(data);
                }
            } catch (error) {
                const status_code = error?.response?.status;

                if (status_code === 401)  {
                    this.$router.push({
                        name: 'accountsLogin',
                        query: { referer: `http://localhost:8080/${this.$router.currentRoute.fullPath}` },
                    });

                    return;
                }

                this.showError(error);
                this.handleError(error);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang='scss'>
.verification-page {
    &__logo {
        margin-bottom: 45px;
        width: 200px;
    }

    &__title {
        font-size: 2.25rem;
        font-weight: 700;
        margin-bottom: 30px;
        color: $general-black;
    }

    &__text {
        color: #787F84;
        font-size: 16px;
        margin-bottom: 30px;
        font-weight: 400;

        b {
            font-weight: 600;
        }
    }

    &__empty-state-location {
        img {
            height: 200px;
            width: auto;
        }
    }
}
</style>
